import React, { useState, useEffect, useRef } from 'react';
import TextArea from 'components/shared/debouncedTextArea';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import request from 'utils/request_ajax';
import { useParams } from 'react-router-dom';
import { routes as ROUTES } from 'constants/config';
import { Drag } from 'components/ui/Icon/Streamline';
import { Icon } from 'ui';
import { debounce } from 'lodash/fp';
import EventList from 'components/editor/element/event/EventList.jsx';
import * as actions from '../../../actions/journeyBuilder';
import { updateBanner, displayBanner } from '../../../actions/journeyBuilder';
import { formatDate } from '../__helpers__/journeyHelper';

const SelectEvent = ({
  step,
  idx,
  listeners,
  attributes,
  handleCancelClick,
}) => {
  const [loading, setLoading] = useState(false);
  const [query] = useState('');

  const { previewing } = useSelector(state => state.ui);
  const [error, setError] = useState(null);
  const { steps, journeyStatus } = useSelector(state => state);

  const dispatch = useDispatch();
  const { journeyId } = useParams();

  const handleSaveEvent = item => {
    const contentAlreadyUsed = steps.find(
      elem =>
        elem.contentId === item.id && elem.contentType === step.contentType,
    );

    const isSelectingSelf = item.id === step.contentId;

    if (contentAlreadyUsed && !isSelectingSelf) {
      setError(t('journeys.builder.event_already_selected'));
      return;
    }
    dispatch(actions.toggleFinishingEditing(true));
    setLoading(true);
    const dispatchContent = {
      index: idx,
      step: {
        contentId: item.id,
        contentType: step.contentType,
        description: step.description,
        id: step.id,
        stepId: step.stepId,
        title: item.name,
        editing: false,
        targetStepId: step.targetStepId,
        event: item,
      },
    };

    if (dispatchContent.step.targetStepId === null) {
      delete dispatchContent.step.targetStepId;
    }

    dispatch(actions.updateStep(dispatchContent));

    if (dispatchContent.step.stepId) {
      request
        .patchJSON(
          ROUTES.JOURNEY.STEPS.RESOURCE(journeyId, dispatchContent.step.stepId),
          {
            content_id: dispatchContent.step.contentId,
            content_type: dispatchContent.step.contentType,
            journey_status: journeyStatus,
          },
        )
        .then(responseData => {
          dispatchContent.step.description = responseData.description;
          dispatch(actions.updateStep(dispatchContent));
          dispatch(actions.toggleFinishingEditing(false));
          dispatch(
            actions.updateAutosavedAt({
              autosavedAt: formatDate(responseData.updatedAt),
            }),
          );
          dispatch(
            actions.updateFields({
              lastUpdater: responseData.lastUpdater,
              updatedAt: responseData.updatedAt,
            }),
          );
        })
        .fail(err => {
          console.error(err);
          dispatch(
            updateBanner({
              message: t('journeys.builder.reorder_error'),
              status: 'error',
            }),
          );
          dispatch(
            displayBanner({
              bannerDismissible: false,
            }),
          );
        });
    } else {
      request
        .postJSON(ROUTES.JOURNEY.STEPS.CREATE(journeyId), {
          content_type: dispatchContent.step.contentType,
          content_id: dispatchContent.step.contentId,
          target_step_id: dispatchContent.step.targetStepId,
          journey_status: journeyStatus,
        })
        .then(responseData => {
          dispatchContent.step.stepId = responseData.stepId;
          dispatchContent.step.id = responseData.stepId;
          dispatchContent.step.description = responseData.description;
          dispatch(actions.updateStep(dispatchContent));
          dispatch(actions.toggleFinishingEditing(false));
          dispatch(
            actions.updateAutosavedAt({
              autosavedAt: formatDate(responseData.updatedAt),
            }),
          );
          dispatch(
            actions.updateFields({
              lastUpdater: responseData.lastUpdater,
              updatedAt: responseData.updatedAt,
            }),
          );
          dispatch(
            actions.updateJourneyStatus({
              journeyStatus: responseData.journeyStatus,
            }),
          );
        })
        .fail(err => {
          console.error(err);
          dispatch(
            updateBanner({
              message: t('journeys.builder.reorder_error'),
              status: 'error',
            }),
          );
          dispatch(
            displayBanner({
              bannerDismissible: false,
            }),
          );
        });
    }
  };

  const [events, setEvents] = useState([]);
  const [selectedSort, setSelectedSort] = useState('0');
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [isSearchResult, setIsSearchResult] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [emptyState, setEmptyState] = useState('');

  const searchInputRef = useRef(null);
  const sortSelectRef = useRef(null);
  const dragContainer = useRef(null);

  const sortOptions = [
    t('lessons.editor.event.recently_updated'),
    t('lessons.editor.event.name_a_z'),
    t('lessons.editor.event.name_z_a'),
  ];

  const renderSortOptions = () => {
    return sortOptions.map((label, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <option key={i} value={i} children={label} aria-label={label} />
    ));
  };

  const getSortParams = value => {
    if (value === '0') {
      return 'sort[direction]=DESC&sort[column]=date_updated';
    }
    if (value === '1') {
      return 'sort[direction]=ASC&sort[column]=name';
    }
    return 'sort[direction]=DESC&sort[column]=name';
  };

  const handleSortChange = event => {
    const { value } = event.target;
    const sortParams = getSortParams(value);
    setLoading(true);
    setSelectedSort(value);
    setPage(1);

    request
      .get(
        `${ROUTES.TRAINING.EVENTS}?${sortParams}&past_or_upcoming=Upcoming%20Events&page=1&per_page=12`,
      )
      .then(res => {
        if (res) {
          const { content, pageCount } = res;
          setEvents(content.filter(eventItem => eventItem.next_session));
          setTotalPages(pageCount);
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    setLoading(true);
    if (isSearchResult) setIsSearchResult(false);
    request
      .get(
        `${ROUTES.TRAINING.EVENTS}?sort[direction]=DESC&sort[column]=date_updated&past_or_upcoming=Upcoming%20Events&page=1&per_page=12`,
      )
      .then(res => {
        if (res) {
          const { content, pageCount } = res;
          if (!content.length) {
            setEmptyState('initial');
          } else {
            setEvents(content.filter(eventItem => eventItem.next_session));
          }
          setTotalPages(pageCount);
          setLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchQuery = useRef(
    debounce(300, event => {
      setLoading(true);
      setSelectedSort('0');
      setPage(1);
      setIsSearchResult(true);
      request
        .get(
          `${ROUTES.TRAINING.EVENTS}?query=${event}&sort[direction]=DESC&past_or_upcoming=Upcoming%20Events&page=1&per_page=12`,
        )
        .then(res => {
          if (res) {
            const { content, pageCount } = res;
            if (content.length) {
              setEmptyState('search');
            }
            setEvents(content.filter(eventItem => eventItem.next_session));
            setTotalPages(pageCount);
            setLoading(false);
          }
        });
    }),
  ).current;

  const getNextPage = () => {
    if (!showLoadMore) {
      setShowLoadMore(true);

      const { value } = sortSelectRef.current;
      const sortParams = getSortParams(value);

      const route = isSearchResult
        ? `${ROUTES.TRAINING.EVENTS}?query=${
            searchInputRef.current?.value
          }&${sortParams}&past_or_upcoming=Upcoming%20Events&page=${
            page + 1
          }&per_page=12`
        : `${
            ROUTES.TRAINING.EVENTS
          }?${sortParams}&past_or_upcoming=Upcoming%20Events&page=${
            page + 1
          }&per_page=12`;
      request.get(route).then(res => {
        if (res) {
          const { content } = res;
          setPage(prev => prev + 1);
          setEvents(prev => [...prev, ...content]);
          setShowLoadMore(false);
        }
      });
    }
  };

  const journeyContentTitle = classNames('JourneyContent-title', {
    'JourneyContent-titleBuilder': !previewing,
  });

  return (
    <div className="JourneyContent JourneyContentPicker">
      <div ref={dragContainer}>
        <div className="JourneyContent-header">
          <div className="JourneyContentPicker-titleContainer">
            <div {...listeners} {...attributes}>
              <Drag className="JourneyContent-icon--drag" size={16} />
            </div>
            <div className={journeyContentTitle}>
              {t('journeys.builder.select_event')}
            </div>
          </div>
          <button
            type="button"
            className="u-linkBtn u-linkBtn--dark td-u"
            children="Cancel"
            onClick={handleCancelClick}
          />
        </div>
        <div className="JourneyContentPicker-body">
          <div className="EditorElement-body">
            <div>
              <TextArea
                onChange={handleSearchQuery}
                value={query}
                className="AssignModal-searchInput"
                placeholder="Search content"
                singleLine
                maxLength="255"
                ref={searchInputRef}
              />
              <div className="AssignModal-sortButton d-f jc-e -mt-4 mb-16">
                <select
                  aria-label="Sort Events"
                  className="NewButton NewButton--white"
                  onChange={handleSortChange}
                  value={selectedSort}
                  children={renderSortOptions()}
                  ref={sortSelectRef}
                />
                <div className="AssignModal-sortIcon">
                  <Icon name="sort" />
                </div>
              </div>
            </div>
            {error && (
              <div className="CommentForm-errorMessage mb-12">{error}</div>
            )}
            <div>
              <EventList
                events={events}
                loading={loading}
                setSelectedEvent={handleSaveEvent}
                page={page}
                totalPages={totalPages}
                getNextPage={getNextPage}
                showLoadMore={showLoadMore}
                emptyState={emptyState}
                previewing
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectEvent;
